import { Button, Dialog, Input, Typography, Textarea, IconButton } from "@material-tailwind/react";
import { HiX, HiPlus } from "react-icons/hi";
import { useEffect, useState } from "react";
import IngredientAPI from '../../api/ingredient';
import { toast } from 'react-toastify';
import { generateUri, validateUri } from '../../utils/uri';
import { useDialog } from '../../context/DialogContext';

/**
 * A reusable dialog component for creating or editing ingredients
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls dialog visibility
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onSuccess - Callback when operation succeeds, returns the created/updated ingredient
 * @param {string} [props.mode="create"] - "create" or "edit"
 * @param {string} [props.ingredientUri] - URI of ingredient to edit (required in edit mode)
 */
const IngredientDialog = ({ isOpen, onClose, onSuccess, mode = "create", ingredientUri }) => {
  const [loading, setLoading] = useState(false);
  const [ingredient, setIngredient] = useState({
    name: '',
    uri: '',
    codes: ''
  });
  const { openTradenameDialog } = useDialog();

  useEffect(() => {
    if (isOpen && mode === "edit" && ingredientUri) {
      fetchIngredient();
    } else if (isOpen && mode === "create") {
      // Reset form when opening in create mode
      setIngredient({
        name: '',
        uri: '',
        codes: ''
      });
    }
  }, [isOpen, mode, ingredientUri]);

  const fetchIngredient = async () => {
    try {
      setLoading(true);
      const response = await IngredientAPI.getOne(ingredientUri);

      // Format codes string if codes exist
      let codesStr = '';
      if (response.codes && response.codes.length > 0) {
        codesStr = response.codes.map(code =>
          `${code.type}: ${code.values.join(', ')}`
        ).join('\n');
      }

      setIngredient({
        ...response,
        codes: codesStr
      });
    } catch (error) {
      toast.error('Failed to fetch ingredient details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Parse the codes string into the expected format
      const parsedCodes = [];
      if (ingredient.codes) {
        const codeLines = ingredient.codes.split('\n');
        codeLines.forEach(line => {
          if (line.trim()) {
            const [type, valuesStr] = line.split(':');
            if (type && valuesStr) {
              const values = valuesStr.split(',').map(v => v.trim()).filter(v => v);
              if (values.length > 0) {
                parsedCodes.push({
                  type: type.trim(),
                  values
                });
              }
            }
          }
        });
      }

      const ingredientData = {
        ...ingredient,
        codes: parsedCodes
      };

      let response;
      if (mode === "edit") {
        response = await IngredientAPI.edit(ingredient._id, ingredientData);
        toast.success('Ingredient updated successfully');
      } else {
        response = await IngredientAPI.create(ingredientData);
        toast.success('Ingredient created successfully');
      }

      if (onSuccess) {
        onSuccess(response);
      }

      onClose();
    } catch (error) {
      toast.error(`Failed to ${mode} ingredient`);
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;

    setIngredient(prev => {
      // Only auto-generate URI if in create mode or if URI is empty
      const shouldUpdateUri = mode === "create" || !prev.uri;
      return {
        ...prev,
        name,
        ...(shouldUpdateUri ? { uri: generateUri(name) } : {})
      };
    });
  };

  const handleUriChange = (e) => {
    const uri = e.target.value;
    if (validateUri(uri)) {
      setIngredient(prev => ({
        ...prev,
        uri
      }));
    }
  };

  // Add function for opening tradename dialog with this ingredient pre-selected
  const handleAddToTradename = () => {
    openTradenameDialog({
      mode: 'create',
      initialIngredients: [{
        _id: ingredient._id,
        name: ingredient.name,
        uri: ingredient.uri
      }],
      onSuccess: (newTradename) => {
        if (newTradename) {
          toast.success(`Ingredient ${ingredient.name} added to new tradename ${newTradename.name}`);
        }
      }
    });
  };

  return (
    <Dialog
      open={isOpen}
      handler={onClose}
      size="lg"
    >
      <Dialog.Overlay>
        <Dialog.Content>
          <div className="p-0">
            <div className="flex items-center justify-between p-4 border-b">
              <Typography variant="h5">
                {mode === "edit" ? "Edit Ingredient" : "Create New Ingredient"}
              </Typography>
              <IconButton onClick={onClose} size="sm">
                <HiX className="w-5 h-5" />
              </IconButton>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Name *</div>
                    <Input
                      type="text"
                      value={ingredient.name}
                      onChange={handleNameChange}
                      disabled={loading}
                      required
                    />
                  </div>

                  <div>
                    <div className="mb-2 font-medium text-gray-700">URI *</div>
                    <Input
                      type="text"
                      value={ingredient.uri}
                      onChange={handleUriChange}
                      disabled={loading || mode === "edit"}
                      required
                      error={!validateUri(ingredient.uri)}
                    />
                    {!validateUri(ingredient.uri) && <p className="mt-1 text-xs text-red-500">URI cannot contain whitespace or special characters</p>}
                  </div>
                </div>

                <div>
                  <div className="mb-2 font-medium text-gray-700">Codes</div>
                  <Textarea
                    value={ingredient.codes}
                    onChange={(e) => setIngredient(prev => ({ ...prev, codes: e.target.value }))}
                    disabled={loading}
                    placeholder="ATC: A01BC23"
                    rows={4}
                  />
                  <Typography className="mt-1 text-sm text-gray-500">
                    Enter one code per line in format: "Type: Value" (e.g., "ATC: A01BC23")
                  </Typography>
                </div>

                {mode === 'edit' && (
                  <div className="mt-4">
                    <Button
                      onClick={handleAddToTradename}
                      type="button"
                      className="flex items-center gap-2"
                    >
                      <HiPlus className="w-4 h-4" /> Add to New Tradename
                    </Button>
                  </div>
                )}
              </form>
            </div>

            <div className="flex justify-end gap-4 p-4 border-t">
              <Button
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !ingredient.name || !ingredient.uri || !validateUri(ingredient.uri)}
                color="primary"
              >
                {loading ? 'Processing...' : mode === "edit" ? 'Save Changes' : 'Create Ingredient'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
};

export default IngredientDialog; 